// Virtual background effects for local video streams
// Uses MediaPipe Selfie Segmentation (lazy-loaded from CDN) to apply
// background blur or replacement. If anything fails, returns the
// original MediaStream unchanged so calls always work.

import { BG_IMAGES } from './backgroundSettings';

let loadPromise = null;
let segmenter = null;

const MEDIAPIPE_CDN = 'https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/selfie_segmentation.js';

function loadScriptOnce(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing && existing.dataset.loaded === 'true') {
      resolve();
      return;
    }
    if (existing) {
      // Script tag exists but may still be loading (or previously hung) - attach listeners to it
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load MediaPipe script (existing tag)')));
      return;
    }

    const timeoutMs = 15000;
    const timeoutId = setTimeout(() => {
      console.warn('[BackgroundEffects] Script load timed out after', timeoutMs, 'ms:', src);
      reject(new Error(`Timed out loading MediaPipe script after ${timeoutMs}ms - check network/CDN access to cdn.jsdelivr.net`));
    }, timeoutMs);

    const script = document.createElement('script');
    script.src = src;
    script.crossOrigin = 'anonymous';
    script.async = true;
    script.onload = () => {
      clearTimeout(timeoutId);
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = (e) => {
      clearTimeout(timeoutId);
      console.warn('[BackgroundEffects] Script tag onerror fired:', e);
      reject(new Error('Failed to load MediaPipe script (onerror)'));
    };
    document.body.appendChild(script);
  });
}

async function ensureSegmenter() {
  if (segmenter) return segmenter;
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    try {
      await loadScriptOnce(MEDIAPIPE_CDN);
      const SelfieSegmentation = window.SelfieSegmentation;
      if (!SelfieSegmentation) throw new Error('SelfieSegmentation not available on window');

      const s = new SelfieSegmentation({
        locateFile: (file) => {
          return `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`;
        }
      });
      s.setOptions({
        modelSelection: 1, // 1 = landscape (people) model, slightly more accurate
        selfieMode: true
      });

      let latestResults = null;
      s.onResults((results) => {
        latestResults = results;
      });

      // Monkey-patch a fire-and-forget helper for easy single-frame processing
      s.processOne = async (input) => {
        await s.send({ image: input });
        return latestResults;
      };

      segmenter = s;
      console.log('[BackgroundEffects] MediaPipe SelfieSegmentation loaded successfully');
      return s;
    } catch (e) {
      loadPromise = null;
      console.warn('[BackgroundEffects] Failed to load SelfieSegmentation:', e);
      throw e;
    }
  })();

  return loadPromise;
}

function createHiddenVideo(srcObject, width, height) {
  const video = document.createElement('video');
  video.playsInline = true;
  video.muted = true;
  video.autoplay = true;
  video.width = width;
  video.height = height;
  video.srcObject = srcObject;
  // IMPORTANT: keep this element within the viewport bounds (just invisible via
  // opacity/pointer-events). Positioning it far off-screen (e.g. left: -9999px)
  // can cause some browsers to treat it as "not intersecting the viewport" and
  // throttle/suspend video decoding for power-saving, which silently starves
  // drawImage() of real frames (blank canvas output).
  video.style.position = 'fixed';
  video.style.top = '0';
  video.style.left = '0';
  video.style.width = '2px';
  video.style.height = '2px';
  video.style.opacity = '0';
  video.style.pointerEvents = 'none';
  video.style.zIndex = '-1';
  document.body.appendChild(video);
  video.play?.().catch((e) => console.warn('[BackgroundEffects] hidden video play() failed:', e));
  return video;
}

function createOffscreenCanvas(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.style.position = 'fixed';
  canvas.style.top = '0';
  canvas.style.left = '0';
  canvas.style.width = '2px';
  canvas.style.height = '2px';
  canvas.style.opacity = '0';
  canvas.style.pointerEvents = 'none';
  canvas.style.zIndex = '-1';
  document.body.appendChild(canvas);
  return canvas;
}

function createImageCache() {
  const cache = {};
  return {
    get(src) {
      if (cache[src]) return cache[src].loaded ? cache[src].img : null;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = src;
      const entry = { img, loaded: false };
      img.onload = () => { entry.loaded = true; };
      img.onerror = () => { console.warn('[BackgroundEffects] failed to load bg image', src); };
      cache[src] = entry;
      return null;
    }
  };
}

const imageCache = createImageCache();

/**
 * Wraps a MediaStream with a virtual background effect.
 * @param {MediaStream} inputStream - raw camera stream
 * @param {Object} settings
 * @param {boolean} settings.enabled
 * @param {string} settings.mode - 'blur' | 'image'
 * @param {number} settings.selectedIndex - 0-5, index into BG_IMAGES
 * @returns {Promise<{stream: MediaStream, stop: Function}>}
 */
export async function addBg(inputStream, settings = {}) {
  console.log('[BackgroundEffects] addBg() called with settings:', settings, 'videoTracks:', inputStream?.getVideoTracks?.().length);
  if (!settings.enabled) {
    console.log('[BackgroundEffects] Skipping - settings.enabled is false');
    return { stream: inputStream, stop: () => {} };
  }

  // Validate input stream has a video track
  const [videoTrack] = inputStream.getVideoTracks();
  if (!videoTrack) {
    console.warn('[BackgroundEffects] Skipping - no video track on input stream');
    return { stream: inputStream, stop: () => {} };
  }

  const audioTracks = inputStream.getAudioTracks();

  try {
    const s = await ensureSegmenter();

    // Use the track's actual settings or a tiny fallback that matches our call constraints
    const trackSettings = videoTrack.getSettings();
    const width = trackSettings.width || 140;
    const height = trackSettings.height || 220;

    const inputVideo = createHiddenVideo(inputStream, width, height);
    await new Promise((resolve, reject) => {
      if (inputVideo.readyState >= 2) {
        resolve();
      } else {
        inputVideo.onloadeddata = resolve;
        setTimeout(() => reject(new Error('input video load timeout')), 5000);
      }
    });

    const canvas = createOffscreenCanvas(width, height);
    const ctx = canvas.getContext('2d');

    // Off-DOM scratch canvas used to cut the sharp "person" pixels out of the
    // raw frame (via the segmentation mask) before compositing them on top of
    // the processed background. Drawing the full sharp frame directly onto
    // the main canvas would just overwrite the background entirely, so this
    // intermediate step is required.
    const cutoutCanvas = document.createElement('canvas');
    cutoutCanvas.width = width;
    cutoutCanvas.height = height;
    const cutoutCtx = cutoutCanvas.getContext('2d');

    // Start a canvas.captureStream()
    const outputStream = canvas.captureStream(24);

    let running = true;
    let rafId = null;
    let loggedNoMask = false;

    function drawPersonCutout(results, sharpSource) {
      cutoutCtx.clearRect(0, 0, width, height);
      cutoutCtx.globalCompositeOperation = 'source-over';
      cutoutCtx.drawImage(sharpSource, 0, 0, width, height);
      cutoutCtx.globalCompositeOperation = 'destination-in';
      cutoutCtx.drawImage(results.segmentationMask, 0, 0, width, height);
      cutoutCtx.globalCompositeOperation = 'source-over';
      return cutoutCanvas;
    }

    async function drawFrame() {
      if (!running) return;
      let saved = false;
      try {
        const results = await s.processOne(inputVideo);
        if (!results?.segmentationMask && !loggedNoMask) {
          loggedNoMask = true;
          console.warn('[BackgroundEffects] No segmentation mask returned yet - falling back to raw frame until model produces results');
        }
        // Prefer MediaPipe's own synced output frame (results.image) over the
        // live <video> element - it's guaranteed to correspond exactly to the
        // frame that was segmented, avoiding any decode/timing mismatch.
        const sharpSource = results?.image || inputVideo;

        ctx.save();
        saved = true;
        ctx.globalCompositeOperation = 'source-over';

        if (settings.mode === 'blur') {
          // Draw a blurred version of the raw camera frame as the background
          ctx.filter = 'blur(12px)';
          ctx.drawImage(sharpSource, 0, 0, width, height);
          ctx.filter = 'none';

          if (results && results.segmentationMask) {
            // Cut the sharp person out of the raw frame, then composite just
            // that cutout on top of the blurred background (the transparent
            // areas of the cutout leave the blurred background untouched).
            const cutout = drawPersonCutout(results, sharpSource);
            ctx.drawImage(cutout, 0, 0, width, height);
          }
        } else if (settings.mode === 'image') {
          // Look up the selected background image every frame - it may still be
          // loading asynchronously on earlier frames, so re-check until it's ready.
          const selectedImageUrl = imageCache.get(BG_IMAGES[settings.selectedIndex || 0]);
          // Fill the background
          if (selectedImageUrl && selectedImageUrl.complete && selectedImageUrl.naturalWidth) {
            drawCoverImage(ctx, selectedImageUrl, width, height);
          } else {
            ctx.fillStyle = '#111827';
            ctx.fillRect(0, 0, width, height);
          }

          if (results && results.segmentationMask) {
            const cutout = drawPersonCutout(results, sharpSource);
            ctx.drawImage(cutout, 0, 0, width, height);
          }
        }

      } catch (err) {
        console.warn('[BackgroundEffects] draw error:', err);
      } finally {
        if (saved) ctx.restore();
      }
      if (running) {
        rafId = requestAnimationFrame(drawFrame);
      }
    }

    // Kick off the loop
    drawFrame();

    const stop = () => {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      if (inputVideo) {
        inputVideo.pause();
        inputVideo.srcObject = null;
        if (inputVideo.parentNode) inputVideo.parentNode.removeChild(inputVideo);
      }
      if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
    };

    // Build the final stream: processed video + original audio
    const videoOutputTrack = outputStream.getVideoTracks()[0];
    const finalStream = new MediaStream();
    if (videoOutputTrack) finalStream.addTrack(videoOutputTrack);
    audioTracks.forEach(t => finalStream.addTrack(t));

    return { stream: finalStream, stop };
  } catch (e) {
    console.warn('[BackgroundEffects] Falling back to original stream:', e);
    return { stream: inputStream, stop: () => {} };
  }
}

function drawCoverImage(ctx, img, width, height) {
  const imgRatio = img.naturalWidth / img.naturalHeight;
  const canvasRatio = width / height;
  let drawWidth = width;
  let drawHeight = height;
  let offsetX = 0;
  let offsetY = 0;

  if (imgRatio > canvasRatio) {
    drawHeight = height;
    drawWidth = img.naturalWidth * (height / img.naturalHeight);
    offsetX = (width - drawWidth) / 2;
  } else {
    drawWidth = width;
    drawHeight = img.naturalHeight * (width / img.naturalWidth);
    offsetY = (height - drawHeight) / 2;
  }

  ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
}

// Compatibility re-export
export default addBg;
